import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

// components
import Loader from '../../components/Loader/Loader';
import LeadBankOffers from '../../components/CreditApplications/LeadBankOffers';
import LeadCreditApplicationDetails from '../../components/CreditApplications/LeadCreditApplicationDetails';
import Status from '../../components/Status';
import tableStyles from '../../components/Table/table.module.css';
import arrow from '../../assets/arrow.svg';
import leadDealStatusColor from '../../utils/leadDealStatusColor';
import leadBankSummary from '../../utils/leadBankSummary';
import { colors } from '../../constants/colors';
import { BANK_CREDIT_ACCEPTED } from '../../constants/bankCreditStatuses';

// api
import getLeadCard from '../../api/leads/getLeadCard';
import formatDate from '../../utils/formatDate';
import formatNumber from '../../utils/formatNumber';
import fixDateTimezone from '../../utils/fixDateTimezone';

const formatRemaining = (ms) => {
  if (ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
};

// Даты сделок приходят в UTC; в списке лидов они уже показываются через эту пару
// утилит, а тут дата, снятая на голом new Date(), у вечерних сделок уезжала на день.
const dealDate = (value) => (value ? formatDate(fixDateTimezone(value)) : '');

const TtlCountdown = ({ expiresAt }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  if (!expiresAt) return null;
  const remaining = formatRemaining(new Date(expiresAt).getTime() - now);
  if (!remaining) return null;

  return (
    <div style={{ background: '#FFF6E5', border: '1px solid #F5C56B', borderRadius: 8, padding: '10px 16px', marginBottom: 20 }}>
      Банки ещё не ответили — осталось {remaining}.
    </div>
  );
};

const styles = {
  card: { padding: 24, border: '1px solid #E7E7ED', borderRadius: 8, background: '#FFFFFF', marginTop: 20 },
  cardTitle: { fontWeight: 600, fontSize: 15, margin: '0 0 16px' },
  scroll: { overflowX: 'auto' },
  nowrap: { whiteSpace: 'nowrap', wordBreak: 'normal' },
  current: { marginLeft: 8, color: '#6A6A6A', fontSize: 12, fontWeight: 400 },
  sub: { color: '#6A6A6A', fontSize: 12, lineHeight: '16px' },
  open: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    // Бежевый по белому — контраст ~2:1, для действий берём тёмный цвет бренда
    color: 'var(--brand-dark)',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    whiteSpace: 'nowrap',
  },
  // Область клика 32×32: стрелка в 8px глифе попадала мышью только с третьей попытки
  toggle: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 4 },
  chevron: { display: 'block', transition: 'transform 0.15s ease-out' },
  details: { padding: '8px 24px 24px', background: '#FFFFFF' },
  detailsTitle: { fontWeight: 600, fontSize: 14, margin: '16px 0 12px' },
  muted: { color: '#8B8B8B', fontSize: 14 },
  pastToggle: {
    marginTop: 12,
    padding: 0,
    color: 'var(--brand-dark)',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
  },
};

// «ещё 1 сделку», «ещё 2 сделки», «ещё 5 сделок»
const pastLabel = (n) => {
  const last = n % 10;
  if (n % 100 >= 11 && n % 100 <= 14) return 'сделок';
  if (last === 1) return 'сделку';
  if (last >= 2 && last <= 4) return 'сделки';
  return 'сделок';
};

const DealRow = ({ deal, current, status, expanded, onToggle, onOpen }) => {
  const car = deal.car;
  const banks = leadBankSummary(deal.banks);
  return (
    <tr onClick={onOpen} style={current ? { backgroundColor: colors.rowYellowBG } : undefined}>
      <td style={{ width: 40, paddingLeft: 4 }} onClick={(e) => e.stopPropagation()}>
        {current && (
          <button
            type="button"
            style={styles.toggle}
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? 'Свернуть заявку и банки' : 'Показать заявку и банки'}
          >
            <img src={arrow} alt="" style={{ ...styles.chevron, transform: expanded ? 'none' : 'rotate(-90deg)' }} />
          </button>
        )}
      </td>
      <td style={styles.nowrap}>
        <b>№{deal.id}</b>
        {current && <span style={styles.current}>текущая</span>}
      </td>
      <td style={styles.nowrap}>{dealDate(deal.createdAt)}</td>
      <td>
        {car ? (
          <>
            <div>{[car.mark, car.model].filter(Boolean).join(' ')}</div>
            <div style={styles.sub}>
              {[car.complectation, car.price ? `${formatNumber(car.price)} ₸` : null].filter(Boolean).join(' · ')}
            </div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td style={{ minWidth: 190, maxWidth: 260 }}>
        {status.title && <Status status={status.title} color={leadDealStatusColor(status)} />}
      </td>
      <td>
        {banks.length ? (
          banks.map((part, i) => (
            <span key={part.text} style={{ color: part.color }}>
              {i > 0 && <span style={{ color: '#B4B4B4' }}> · </span>}
              {part.text}
            </span>
          ))
        ) : (
          <span style={styles.sub}>заявок нет</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()} style={{ paddingRight: 12, textAlign: 'right' }}>
        <Link to={`/deals/deal/${deal.id}/main-info`} style={styles.open}>
          Открыть
          <img src={arrow} alt="" style={{ transform: 'rotate(-90deg)' }} />
        </Link>
      </td>
    </tr>
  );
};

/**
 * Таб «Сделки» на карточке лида: все сделки лида таблицей, как в разделе «Сделки».
 * У лида их бывает несколько (старая просрочена по TTL, завели новую), поэтому в строке
 * не только номер, но и машина, дата и ответы банков — чтобы было понятно, какая
 * сделка какая. Текущая (последняя) выделена; по стрелке раскрываются данные заявки и
 * банки с выбором предложения. Клик по строке открывает сделку.
 */
const LeadDealInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState();
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const deals = lead?.deals || [];
  const activeDeal = deals[0];
  // Прошлые сделки — история (бросили анкету, начали заново, просрочили): по умолчанию свёрнуты.
  const pastCount = deals.length - 1;
  const visibleDeals = showPast ? deals : deals.slice(0, 1);

  const init = () => {
    getLeadCard({ id })
      .then((res) => setLead(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    init();
  }, [id]);

  if (loading) return <Loader />;

  if (!activeDeal) {
    return (
      <div style={styles.card}>
        <p style={styles.muted}>По этому лиду ещё нет сделки — заявки в банки не отправлялись.</p>
      </div>
    );
  }

  // Бейдж текущей сделки — по факту одобрения из заявок (lead.dealStatus), как в списке лидов.
  const statusOf = (deal, index) =>
    index === 0 && lead.dealStatus
      ? lead.dealStatus
      : { id: deal.statusId, title: deal.status?.title, bankApproved: !!deal.banks?.[BANK_CREDIT_ACCEPTED] };

  return (
    <div style={styles.card}>
      {activeDeal.ttlExpiresAt && <TtlCountdown expiresAt={activeDeal.ttlExpiresAt} />}

      <p style={styles.cardTitle}>Сделки лида</p>
      <div style={styles.scroll}>
        <table className={tableStyles.table} style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th>Сделка</th>
              <th>Дата</th>
              <th>Автомобиль</th>
              <th>Этап</th>
              <th>Банки</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleDeals.map((deal, index) => (
              <React.Fragment key={deal.id}>
                <DealRow
                  deal={deal}
                  current={index === 0}
                  status={statusOf(deal, index)}
                  expanded={expanded}
                  onToggle={() => setExpanded(!expanded)}
                  onOpen={() => navigate(`/deals/deal/${deal.id}/main-info`)}
                />
                {index === 0 && expanded && (
                  <tr style={{ cursor: 'default' }}>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div style={styles.details}>
                        <p style={styles.detailsTitle}>Данные заявки</p>
                        <LeadCreditApplicationDetails chunkId={activeDeal.chunkId} />
                        <p style={{ ...styles.detailsTitle, marginTop: 28 }}>Банки</p>
                        <LeadBankOffers
                          chunkId={activeDeal.chunkId}
                          dealStatusId={activeDeal.statusId}
                          onOfferSelected={init}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {pastCount > 0 && (
        <button type="button" style={styles.pastToggle} onClick={() => setShowPast(!showPast)}>
          {showPast ? 'Скрыть прошлые сделки' : `Показать ещё ${pastCount} ${pastLabel(pastCount)}`}
        </button>
      )}
    </div>
  );
};

export default LeadDealInfo;
